import React, { useState } from 'react';
import {
  MessageCircle,
  ImageIcon,
  Wand2,
  ArrowLeft,
  ExternalLink,
  ShieldCheck,
} from 'lucide-react';

/**
 * ResearchLabPage — "Scholar's Research Lab · Deep AI Tools"
 *
 * Per Architect Directive (UI/UX Resuscitation, Phase 1, 2026-06-25):
 * DeepAI.org sets `x-frame-options: ALLOWALL` so its tools are fully
 * iframe-embeddable.  This page surfaces three DeepAI surfaces in an
 * academic, Scholar-themed tabbed interface and renders ONLY the active
 * tab's iframe (conditional rendering, not display:none) to avoid
 * loading bandwidth users haven't asked for.
 *
 * Routes: /research · /deep-ai · /tools  (all three resolve here)
 */

const TABS = [
  {
    id: 'chat',
    label: 'AI Chat',
    blurb: 'Research-grade conversational reasoning. Ask questions, draft outlines, stress-test ideas.',
    icon: MessageCircle,
    iframeSrc: 'https://deepai.org/chat',
    title: 'Deep AI Chat — Research',
  },
  {
    id: 'image',
    label: 'Image Generator',
    blurb: 'Text-to-image for course illustrations, lesson diagrams, and visual notes.',
    icon: ImageIcon,
    iframeSrc: 'https://deepai.org/machine-learning-model/text2img',
    title: 'AI Image Generator',
  },
  {
    id: 'edit',
    label: 'Image Editor',
    blurb: 'Refine, restyle, or repair an existing image with prompted edits.',
    icon: Wand2,
    iframeSrc: 'https://deepai.org/machine-learning-model/image-editor',
    title: 'AI Image Editor',
  },
];

export default function ResearchLabPage() {
  const [activeId, setActiveId] = useState(TABS[0].id);
  const active = TABS.find((t) => t.id === activeId) || TABS[0];
  const ActiveIcon = active.icon;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-cyan-500/15 bg-gray-950">
        <div className="container mx-auto px-4 py-5 flex items-center justify-between">
          <a
            href="/university"
            data-testid="research-back-home"
            className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to HumanCodex
          </a>
          <span className="px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] border border-cyan-300/50 text-cyan-200">
            Scholar's Research Lab
          </span>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10">
        {/* Title */}
        <section className="max-w-3xl mx-auto text-center mb-9">
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-cyan-300/85 mb-2">
            Deep AI Tools · Embedded for Education
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight"
            data-testid="research-lab-title"
          >
            Scholar&apos;s <span className="text-cyan-300">Research Lab</span>
          </h1>
          <p className="mt-4 text-base text-gray-300 leading-relaxed">
            Three live tools from DeepAI, embedded directly inside the Library so you can
            think, sketch, and refine without ever leaving your study. The Truth Gate still
            applies — verify generated outputs before quoting them.
          </p>
        </section>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Deep AI tool tabs"
          className="flex flex-wrap justify-center gap-2 mb-5"
          data-testid="research-lab-tabs"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.id === activeId;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                aria-controls={`tab-panel-${t.id}`}
                id={`tab-button-${t.id}`}
                type="button"
                onClick={() => setActiveId(t.id)}
                data-testid={`research-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-mono uppercase tracking-[0.16em] transition-colors border-2 ${
                  isActive
                    ? 'bg-cyan-400 text-black border-cyan-200 shadow-[0_0_18px_rgba(34,211,238,0.45)]'
                    : 'border-cyan-300/30 text-cyan-200 hover:border-cyan-300/70 hover:bg-cyan-500/10'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab blurb + open-in-new-tab link */}
        <div className="max-w-3xl mx-auto mb-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-1">
          <div className="text-sm text-gray-300 inline-flex items-start gap-2">
            <ActiveIcon className="w-4 h-4 mt-0.5 text-cyan-300 flex-shrink-0" />
            <span>{active.blurb}</span>
          </div>
          <a
            href={active.iframeSrc}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`research-tab-newtab-${active.id}`}
            className="text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-300 hover:text-cyan-200 border border-cyan-300/30 hover:border-cyan-300/60 rounded-full px-3 py-1 transition-colors whitespace-nowrap inline-flex items-center gap-1"
          >
            Open at DeepAI <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Active iframe panel (conditional, not display:none) */}
        <section
          role="tabpanel"
          id={`tab-panel-${active.id}`}
          aria-labelledby={`tab-button-${active.id}`}
          data-testid={`research-lab-panel-${active.id}`}
          className="rounded-2xl border-2 border-cyan-400/30 bg-gradient-to-br from-cyan-950/30 via-gray-900 to-black p-2 sm:p-3"
          style={{ boxShadow: '0 0 28px rgba(34,211,238,0.15)' }}
        >
          <iframe
            key={active.id}
            src={active.iframeSrc}
            title={active.title}
            width="100%"
            height="700"
            loading="lazy"
            allow="clipboard-write"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid={`research-lab-iframe-${active.id}`}
            style={{
              border: 'none',
              borderRadius: '12px',
              width: '100%',
              minHeight: '700px',
              background: '#0a0a0a',
              display: 'block',
            }}
          />
        </section>

        {/* Truth Gate footer */}
        <section className="max-w-3xl mx-auto mt-8 text-center">
          <div className="inline-flex items-center gap-2 text-xs text-gray-500">
            <ShieldCheck className="w-3.5 h-3.5 text-cyan-400/70" />
            <span>
              DeepAI tools are third-party. Outputs pass through the 95% Truth Gate before
              being treated as canon — verify before citing in coursework.
            </span>
          </div>
        </section>
      </main>
    </div>
  );
}
